import CameraControls from "camera-controls";
import * as THREE from "three";
import {
  Box3,
  MathUtils,
  OrthographicCamera,
  PerspectiveCamera,
  Vector3
} from "three";

import { Square, fileOf, rankOf } from "src/api";

import { CAMERA_MIN_PITCH_DEGREES } from "../../game/gameConstants";

// DO NOT REMOVE THIS LINE, IT IS ESSENTIAL FOR THE LIB
// TO PROPERLY INITIALIZE!
CameraControls.install({ THREE });

// Use this signature to reference BoardCamera instances between handoffs.
export type BoardCameraAPI = {
  camera: PerspectiveCamera | OrthographicCamera;
  controls: CameraControls;
  update(dtSeconds: number): void;
  focusSquares(...squares: Square[]): Promise<void>;
};

// Class implementation for the board camera.
export class BoardCamera implements BoardCameraAPI {
  public readonly camera: PerspectiveCamera | OrthographicCamera;
  public readonly domElement?: HTMLElement;
  public readonly controls: CameraControls;
  constructor(
    camera: PerspectiveCamera | OrthographicCamera,
    domElement?: HTMLElement
  ) {
    this.camera = camera;
    this.domElement = domElement;
    this.controls = new CameraControls(camera, domElement);
    this.controls.smoothTime = 0.4;
    this.controls.maxPolarAngle = MathUtils.degToRad(
      90 - CAMERA_MIN_PITCH_DEGREES
    );
    this.controls.mouseButtons.left = CameraControls.ACTION.ROTATE;
    this.controls.touches.one = CameraControls.ACTION.TOUCH_ROTATE;
    this.doInitialCameraSetup();
  }
  private async doInitialCameraSetup() {
    await this.controls.setLookAt(20, 32, 12, 10.5, 0, -3.5, false);
    await this.controls.setLookAt(20, 32, 12, 3.5, 0, -3.5, true);
  }
  async focusSquares(...squares: Square[]): Promise<void> {
    const bbox = new Box3();
    for (const square of squares) {
      const rank = rankOf(square);
      const file = fileOf(square);
      const pos = new Vector3(rank, 0, file);
      bbox.expandByPoint(pos);
    }
    if (bbox.isEmpty()) return;
    await this.controls.fitToBox(bbox, true);
  }
  update(dtSeconds: number) {
    this.controls.update(dtSeconds);
  }
}
